import React, {createContext, useContext} from 'react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const CategoryUrlContext = createContext({})

const localizeInternalPath = (url, buildUrl) => {
    if (!url || !url.startsWith('/')) {
        return url
    }

    return buildUrl(url)
}

/**
 * Hook to get category URL map - kept for backwards compatibility
 * SSR now resolves category URLs, so this returns an empty object
 */
export const useCategoryUrlMap = () => {
    const context = useContext(CategoryUrlContext)
    if (!context) {
        console.warn('useCategoryUrlMap must be used within a CategoryUrlMapProvider')
        return {}
    }
    return context
}

/**
 * Hook to resolve link data from Optimizely CMS blocks
 * Relies on SSR-resolved URLs (resolvedUrl property) for CategoryLinkBlock and ProductLinkBlock
 *
 * @param {Object} [options]
 * @param {boolean} [options.localize=true] - Apply buildUrl locale prefix to internal paths.
 *   Keep true for RouterLink CMS consumers. Set false when the consumer uses PWA Kit <Link>,
 *   which already localizes (e.g. footer, empty cart) — otherwise locale prefixes double.
 */
export const useGetLinkData = ({localize = true} = {}) => {
    const {buildUrl} = useMultiSite()

    return (linkProps = {}) => {
        const linkData = {
            ...linkProps,
            generatedURL: null
        }

        switch (linkProps?.__typename) {
            case 'CategoryLinkBlock': {
                // Use pre-resolved URL from SSR; localize for RouterLink CMS CTAs
                const categoryUrl = linkProps?.resolvedUrl || null
                linkData.generatedURL =
                    categoryUrl && localize
                        ? localizeInternalPath(categoryUrl, buildUrl)
                        : categoryUrl
                linkData.isExternalUrl = false
                break
            }
            case 'ScreenLinkBlock': {
                const screenIdMapping = {
                    'navigation_find_a_store': '/stores',
                    'navigation_signin': '/login'
                }
                let screenUrl = screenIdMapping[linkProps?.ScreenId] || linkProps?.ScreenId

                // Ensure URL starts with "/" and ends with ".html"
                if (screenUrl && !screenUrl.startsWith('/')) {
                    screenUrl = '/' + screenUrl
                }
                if (screenUrl && !screenUrl.endsWith('.html')) {
                    screenUrl = screenUrl + '.html'
                }

                // Localize for RouterLink content pages; skip for PWA Kit <Link> consumers
                linkData.generatedURL = localize
                    ? localizeInternalPath(screenUrl, buildUrl)
                    : screenUrl
                linkData.isExternalUrl = false
                break
            }
            case 'ExternalLinkBlock':
                // Special URI mapping: open:chat triggers Clara Chat instead of navigation
                if (linkProps?.Uri === 'open:chat') {
                    linkData.generatedURL = '#'
                    linkData.isExternalUrl = false
                    linkData.isOpenChat = true
                    linkData.stayOnSamePage = true
                    break
                }

                linkData.generatedURL = `${linkProps?.Uri}`

                // Determine if URL is external (starts with http:// or https://)
                linkData.isExternalUrl = linkData.generatedURL?.startsWith('http://') || linkData.generatedURL?.startsWith('https://')

                // Check if it should open in a new tab
                if (linkProps?.StayOnSamePage === true) {
                    linkData.stayOnSamePage = true
                } else {
                    linkData.stayOnSamePage = false
                    linkData.target = '_blank'
                    linkData.rel = 'noopener noreferrer'
                }

                break
            case 'ProductLinkBlock': {
                // Use pre-resolved URL from SSR; localize for RouterLink CMS CTAs
                const productUrl =
                    linkProps?.resolvedUrl || `/product/${linkProps?.ProductId}`
                linkData.generatedURL = localize
                    ? localizeInternalPath(productUrl, buildUrl)
                    : productUrl
                linkData.isExternalUrl = false
                break
            }
            default:
                // Unknown link type - return null so components can handle gracefully
                linkData.generatedURL = null
                linkData.isExternalUrl = false
                break
        }

        return linkData
    }
}

/**
 * Provider component - simplified to just provide empty context
 * SSR handles category URL resolution via resolveCategoryLinkUrls in ssr.js
 */
export const CategoryUrlMapProvider = ({children}) => {
    return (
        <CategoryUrlContext.Provider value={{}}>
            {children}
        </CategoryUrlContext.Provider>
    )
}

export default CategoryUrlContext
